import React from "react";
import styled, { keyframes, css } from "styled-components";
import { useIntersectionObserver } from "../hooks/useIntersectionObserver";

const fadeInUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const rotateIn = keyframes`
  from {
    opacity: 0;
    transform: rotateY(-180deg);
  }
  to {
    opacity: 1;
    transform: rotateY(0);
  }
`;

const SkillsSection = styled.section`
  padding: 3rem 2rem;
  background: ${({ theme }) => theme.surface};
  border-radius: 12px;
  margin: 2rem auto;
  max-width: 1200px;
  box-shadow: ${({ theme }) => theme.card.shadow};
  opacity: 0;
  transform: translateY(30px);
  ${({ isVisible }) =>
    isVisible &&
    css`
      animation: ${fadeInUp} 0.6s ease-out forwards;
    `}

  @media (max-width: 768px) {
    padding: 2rem 1rem;
    margin: 1rem;
  }
`;

const SectionTitle = styled.h2`
  color: ${({ theme }) => theme.text.primary};
  font-size: 2.5rem;
  text-align: center;
  margin-bottom: 3rem;
  animation: ${fadeInUp} 0.6s ease-out;

  @media (max-width: 768px) {
    font-size: 2rem;
    margin-bottom: 2rem;
  }
`;

const SkillsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(160px, 1fr));
  gap: 2rem;
  padding: 1rem;
  perspective: 1000px;

  @media (max-width: 768px) {
    grid-template-columns: repeat(auto-fit, minmax(120px, 1fr));
    gap: 1.5rem;
  }
`;

const SkillItem = styled.div`
  text-align: center;
  padding: 1.5rem;
  background: ${({ theme }) => theme.card.background};
  border-radius: 12px;
  transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
  position: relative;
  overflow: hidden;
  opacity: 0;
  transform-style: preserve-3d;

  ${({ isVisible, index }) =>
    isVisible &&
    css`
      animation: ${rotateIn} 0.6s cubic-bezier(0.4, 0, 0.2, 1) forwards;
      animation-delay: ${index * 0.1}s;
    `}

  &:hover {
    transform: translateY(-10px) scale(1.05);
    box-shadow: ${({ theme }) => `0 20px 30px -15px ${theme.card.shadow}`};
  }

  &::before {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(
      135deg,
      ${({ theme }) => theme.primary}20,
      ${({ theme }) => theme.secondary}20
    );
    border-radius: 12px;
    opacity: 0;
    transition: opacity 0.3s ease;
  }

  &:hover::before {
    opacity: 1;
  }

  .icons {
    font-size: 3rem;
    margin-bottom: 1rem;
    color: ${({ theme }) => theme.primary};
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
    display: block;
    position: relative;
    z-index: 1;

    &.colored {
      filter: drop-shadow(0 4px 6px rgba(0, 0, 0, 0.1));
    }
  }

  &:hover .icons {
    transform: scale(1.15) rotate(360deg);
    color: ${({ theme }) => theme.secondary};
  }

  img.icons {
    width: 4.75rem;
    height: 4.75rem;
    margin: 0 auto 1rem;
    object-fit: contain;
  }

  .iconify {
    width: 3rem;
    height: 3rem;
    display: block;
    margin: 0 auto;
  }

  @media (max-width: 768px) {
    padding: 1.25rem;

    .icons {
      font-size: 2.5rem;
    }

    img.icons {
      width: 3.5rem;
      height: 3.5rem;
    }

    .iconify {
      width: 2.5rem;
      height: 2.5rem;
    }
  }
`;

const SkillName = styled.h3`
  color: ${({ theme }) => theme.text.primary};
  font-size: 1.1rem;
  margin: 0.5rem 0;
  font-weight: 500;
  position: relative;
  z-index: 1;
  transition: color 0.3s ease;

  ${SkillItem}:hover & {
    color: ${({ theme }) => theme.secondary};
  }

  @media (max-width: 768px) {
    font-size: 1rem;
  }
`;

const skillsList = [
  { name: "Applied Machine Learning", icon: "fas fa-robot colored" },
  { name: "Artificial Neural Network", icon: "fas fa-brain colored" },
  { name: "Deep Learning", icon: "fas fa-network-wired colored" },
  { name: "TensorFlow", icon: "devicon-tensorflow-original colored" },
  { name: "Gradient Descent", icon: "fas fa-chart-line colored" },
  { name: "Web Services", icon: "fas fa-cloud colored" },
  { name: "REST API", icon: "fas fa-server colored" },
  { name: "Ruby", icon: "devicon-ruby-plain colored" },
  { name: "AWS", icon: "fab fa-aws colored" },
  { name: "React", icon: "devicon-react-original colored" },
  { name: "Git", icon: "devicon-git-plain colored" },
  { name: "jQuery", icon: "devicon-jquery-plain colored" },
  { name: "TypeScript", icon: "devicon-typescript-plain colored" },
  { name: "Rails", icon: "devicon-rails-plain colored" },
  { name: "GitHub", icon: "devicon-github-plain colored" },
  { name: "Express.js", icon: "devicon-express-original colored" },
  { name: "Docker", icon: "devicon-docker-plain colored" },
  { name: "Redux", icon: "devicon-redux-original colored" },
  {
    name: "Talend",
    type: "image",
    src: "/images/talend.png",
    style: { height: "4.75rem", width: "4.75rem" },
  },
  { name: "Apollo", icon: "iconify", dataIcon: "file-icons:apollo" },
];

const Skills = () => {
  const [sectionRef, isSectionVisible] = useIntersectionObserver({
    threshold: 0.2,
  });

  return (
    <SkillsSection ref={sectionRef} isVisible={isSectionVisible}>
      <SectionTitle>Skills</SectionTitle>
      <SkillsGrid>
        {skillsList.map((skill, index) => (
          <SkillItem
            key={skill.name}
            index={index}
            isVisible={isSectionVisible}
          >
            {skill.type === "image" ? (
              <img
                src={skill.src}
                alt={skill.name}
                style={skill.style}
                className="icons"
              />
            ) : skill.icon === "iconify" ? (
              <span
                className="iconify icons"
                data-icon={skill.dataIcon}
                data-inline="false"
              />
            ) : (
              <i className={`icons ${skill.icon}`} />
            )}
            <SkillName>{skill.name}</SkillName>
          </SkillItem>
        ))}
      </SkillsGrid>
    </SkillsSection>
  );
};

export default Skills;
